/* Copyright 2013 - 2024 Waiterio LLC */

// ports below 1024 are considered privileged ports and require root access to bind

const products2id = {
  authrice: 1,
  waiterio: 2,
  tiramisu: 3,
  imagelato: 4,
  metricsmage: 5,
  multilocale: 6,
  polyblog: 7,
  supovia: 11,
  croncool: 16,
  churncatcher: 21,
  dododentist: 22,
  appskyline: 23,
  veterical: 24,
  wapiworld: 25,
  prosperanova: 26,
  dbmuse: 27,
  gammainvestments: 28,
  invoicevista: 29,
  hairdora: 30,
  doctofam: 31,
  hotelumo: 32,
  barberbrio: 33,
  bonitabook: 34,
  gymflexa: 35,
  opticalora: 36,
  mechaniczen: 37,
  pharmaphoria: 38,
  tutorializer: 39,
  pushello: 40,
  socketino: 41,
  iterola: 42,
}

const services2id = {
  www: 1,
  app: 2,
  api: 3,
  whitelabel: 6,
  analytics: 7,
  tutorials: 8,
  fastlane: 9,
  workflows: 10,
}

const products2extraServices = {
  appskyline: {
    mcp: 4,
    'analytics-mcp': 5,
    // appskyline predates the `images: 4` slot the other products use — mcp
    // already claimed 4 and analytics-mcp 5 — so images gets a two-digit id:
    // server 23120, client 23121.
    images: 12,
  },
  authrice: {
    mcp: 4,
  },
  croncool: {
    mcp: 4,
  },
  dbmuse: {
    mcp: 4,
  },
  doctofam: {
    images: 4,
    mcp: 5,
    'analytics-mcp': 11,
  },
  dododentist: {
    images: 4,
    mcp: 5,
    'analytics-mcp': 11,
    'receptionist-mcp': 12,
  },
  gymflexa: {
    images: 4,
    mcp: 5,
    'analytics-mcp': 11,
  },
  hairdora: {
    images: 4,
    mcp: 5,
    // The staff analytics MCP (hairdora/analytics-mcp). Every single-digit
    // slot for hairdora is claimed (images took 4, mcp 5), so it gets a
    // two-digit id: server 30110, client 30111.
    'analytics-mcp': 11,
  },
  hotelumo: {
    images: 4,
    mcp: 5,
    'analytics-mcp': 11,
  },
  imagelato: {
    mcp: 4,
  },
  invoicevista: {
    mcp: 4,
    'analytics-mcp': 5,
  },
  metricsmage: {
    script: 4,
    mcp: 5,
  },
  multilocale: {
    mcp: 4,
    // The Docusaurus guides. In production they are a separate origin that
    // www's CloudFront routes /developers/docs* to; in development the www
    // dev server proxies the same prefix here, so the one url works in both
    // (see multilocale/www/scripts/develop/client.js).
    developers: 5,
  },
  polyblog: {
    mcp: 4,
    'analytics-mcp': 5,
  },
  prosperanova: {
    mcp: 4,
  },
  pushello: {
    mcp: 4,
  },
  socketino: {
    mcp: 4,
  },
  supovia: {
    widget: 4,
    images: 5,
    intelligence: 6,
    mascot: 11,
    mcp: 12,
    'analytics-mcp': 13,
  },
  tiramisu: {
    mcp: 4,
  },
  tutorializer: {
    mcp: 4,
    'analytics-mcp': 5,
  },
  veterical: {
    images: 4,
    mcp: 5,
    'analytics-mcp': 11,
  },
  waiterio: {
    images: 8,
    install: 4,
    geo: 5,
    // waiterio predates the global `tutorials: 8` slot — `images` already
    // claimed serviceId 8 (and every other single-digit slot is taken), so
    // tutorials gets a two-digit override: server 2110, client 2111.
    tutorials: 11,
    // Same reason as tutorials: every single-digit slot for waiterio is
    // claimed (www 1, app 2, api 3, install 4, geo 5, whitelabel 6,
    // analytics 7, images 8, fastlane 9). Server 2130, client 2131.
    // NOT 12 — that yields client 2121, which is already waiterio/www's
    // client (`${productId}${serviceId}` + its own last two digits).
    // Without an entry here `waiterio/unsubscribe`'s portServer.js resolved
    // to undefined, so its dev server bound a random port and getUrl
    // produced `http://localhost:undefined`.
    unsubscribe: 13,
    // Same story again: server 2140, client 2141. Without an entry here
    // `waiterio/blog`'s portClient.js resolved to undefined, which
    // webpack-dev-server quietly papered over by falling back to its own
    // default 8080 — but vite.config.js's `server.port` and getAssetUrl.js's
    // development URL both need a real number, and the latter would otherwise
    // bake `http://localhost:undefined/index.jsx` into every page
    // `generate:development` renders.
    blog: 14,
    // And again for `waiterio/docs`: server 2150, client 2151. Same failure
    // mode as blog — webpack-dev-server silently fell back to its own 8080
    // when portClient.js resolved to undefined, while vite.config.js's
    // `server.port` (with `strictPort: true`) and getAssetUrl.js's
    // development URL both need a real number.
    docs: 15,
  },
  wapiworld: {
    mcp: 4,
  },
}

const products = Object.keys(products2id)

const ports = {}

for (let p = 0; p < products.length; p += 1) {
  const product = products[p]
  ports[product] = {}

  let extraServices = products2extraServices[product] ?? {}
  let services2idWithExtraServices = { ...services2id, ...extraServices }
  const services = Object.keys(services2idWithExtraServices)

  for (let s = 0; s < services.length; s += 1) {
    const service = services[s]
    const productId = products2id[product]
    const serviceId = services2idWithExtraServices[service]

    // Single-digit serviceIds use the original `${productId}${serviceId}00`
    // server / `…${slice(-2)}` client formula. Two-digit serviceIds (added
    // when the original 1-9 slots ran out, starting with `workflows: 10`)
    // can't fit a trailing "00" without overflowing 65535 for 2-digit
    // productIds, so they get a 1-digit suffix instead: '0' for server,
    // '1' for client.
    let client
    let server

    if (serviceId < 10) {
      client = `${productId}${serviceId}`
      server = `${client}00`
      client += `${client.slice(-2)}`
    } else {
      const prefix = `${productId}${serviceId}`
      server = `${prefix}0`
      client = `${prefix}1`
    }

    ports[product][service] = {
      client,
      server,
    }
  }
}

const clientServices = new Set([
  'app',
  'intelligence',
  'mascot',
  'script',
  'tutorials',
  'widget',
  'www',
])

export function getClientOrServer(service) {
  return clientServices.has(service) ? 'client' : 'server'
}

// ───────────────────────────── per-product Windows CDP debug ports
//
// The native C# / WinUI 3 / WebView2 shells under <product>/windows expose
// Chrome DevTools Protocol so Playwright + raw-WebSocket drivers can attach
// from the macOS host. Each product needs its own port so two Windows apps
// can run side-by-side without WebView2 caching a failed bind.
//
// `<product>/windows/AppConfig.cs:CdpPort` must equal this value. The C#
// const is the runtime source of truth; this JS expression is the design
// document — so the next product added gets a deterministic port without a
// fresh argument about numbering.
//
// Formula: 9200 + productId, so dododentist(22)=9222 (preserves the existing
// port — no migration), hotelumo(32)=9232.
//
// Doesn't fit the `${productId}${serviceId}…` formula above: every single-
// digit `serviceId` slot is already claimed (top-level + extras), and a
// two-digit slot blows past 65535.
for (let p = 0; p < products.length; p += 1) {
  const product = products[p]
  ports[product].windows = {
    cdpPort: String(9200 + products2id[product]),
  }
}

export default ports
